/**
 * Property detail · "Apartments" tab.
 *
 * Lists the owner-owned apartments grouped under a property as a grid of
 * ApartmentCards. The "Add apartment" action opens the ApartmentEditSheet in
 * create mode. Each card drills into the apartment detail page.
 */
import { useMemo, useState } from "react";
import { Building, Plus } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ApartmentCard } from "@/components/admin/v2/ApartmentCard";
import { ApartmentEditSheet } from "@/components/admin/v2/ApartmentEditSheet";
import {
  useApartments,
  useProperty,
  usePropertyRooms,
} from "@/hooks/v2/useProperties";
import { useLessors } from "@/hooks/v2/useLessors";
import { usePropertyRepresentorGaps } from "@/hooks/v2/usePropertyRepresentorGaps";
import { effectiveApartmentOwner } from "@/lib/owner-metrics";
import { usePropertyActiveOccupants } from "@/hooks/v2/useReassignBookings";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  currentMonthKey,
  useApartmentMonthRent,
} from "@/hooks/v2/useApartmentMonthRent";

type Props = {
  propertyId: number;
  /** Live search query, owned by the page-level search bar. Filters the grid
   *  by apartment label, floor and owner name. Empty string = show all. */
  search?: string;
};

export function ApartmentsTab({ propertyId, search = "" }: Props) {
  const { t } = useLanguage();
  const { data: apartments = [], isLoading } = useApartments(propertyId);
  const { data: property } = useProperty(propertyId);
  const { data: lessors } = useLessors();
  const { isSuperAdmin, isOwnerPortalOnly } = useAuth();
  const [addOpen, setAddOpen] = useState(false);
  // Apartments whose contracts would print no "vertreten durch" (COL-466),
  // badged on their cards. Staff-only, like the rest of that requirement.
  const repGaps = usePropertyRepresentorGaps(property, {
    enabled: !isOwnerPortalOnly,
  });
  const missingRepIds = new Set(repGaps.missing.map((a) => a.id));

  // Rooms + active occupants for the whole property so an apartment can be
  // matched by the rooms it contains and by who lives in them. Occupants are
  // only fetched for super admins (same gating as the Rooms tab / reassign).
  const { data: rooms = [] } = usePropertyRooms(propertyId);
  const { data: occupants = new Map() } = usePropertyActiveOccupants(
    isSuperAdmin ? propertyId : null
  );

  // Current-month live rent for every visible room in the property, fetched
  // ONCE here rather than per card — an N-apartment grid would otherwise
  // issue N separate booking/payment/proof query chains for numbers already
  // derivable from one shared room set.
  const visibleRoomIds = useMemo(
    () => rooms.filter((r) => !r.is_hidden).map((r) => r.id),
    // Keyed on id + is_hidden together: an id-only key would miss a room
    // toggling visibility, leaving the shared rent query stale until some
    // unrelated change (room add/remove) happened to refetch it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rooms.map((r) => `${r.id}:${r.is_hidden}`).join(",")]
  );
  const {
    data: monthRent,
    isPending: monthRentPending,
    isError: monthRentErrored,
  } = useApartmentMonthRent(visibleRoomIds, currentMonthKey());
  const monthRentByRoom = useMemo(() => {
    const m = new Map<number, number>();
    for (const row of monthRent?.rows ?? []) {
      if (row.roomId == null || !row.paid) continue;
      m.set(row.roomId, (m.get(row.roomId) ?? 0) + row.amount);
    }
    return m;
  }, [monthRent]);
  const currentMonthRevenueFor = (apt: {
    room_ids: number[];
  }): number | null => {
    if (visibleRoomIds.length === 0) return 0;
    // Unknown rather than a misleading €0 while loading or on a failed fetch
    // (e.g. an RLS hiccup) — the card renders "—" for either case.
    if (monthRentPending || monthRentErrored) return null;
    return apt.room_ids.reduce(
      (sum, id) => sum + (monthRentByRoom.get(id) ?? 0),
      0
    );
  };

  const lessorNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const l of lessors ?? []) m.set(l.id, l.name);
    return m;
  }, [lessors]);

  // Owner name per apartment via the EFFECTIVE lessor — its own, else the one
  // inherited from the property. Reading apartment.lessor_id alone left every
  // inheriting apartment ownerless here while the Owners page counted it.
  //
  // The owner-ACCOUNT argument is omitted deliberately: the
  // apartments_fill_lessor_from_owner trigger keeps lessor_id canonical, so an
  // apartment linked only to an owner portal account still resolves here.
  // Remove that trigger and these rows go back to showing no owner. The one
  // account shape the trigger deliberately won't resolve — several candidate
  // lessors — shows no owner here too, which is the intended signal.
  const ownerNameFor = (a: { lessor_id: string | null }): string | null => {
    const id = effectiveApartmentOwner(a.lessor_id, property?.lessor_id);
    return id ? (lessorNameById.get(id) ?? null) : null;
  };

  // apartmentId → lower-cased haystack of its room titles and each room's
  // active tenant name + email. Lets a search for a tenant or a room surface
  // the apartment that contains them.
  const roomTenantIndex = useMemo(() => {
    const m = new Map<number, string>();
    for (const r of rooms) {
      if (r.apartment_id == null) continue;
      const occ = occupants.get(r.id);
      const parts = [r.title, occ?.name ?? "", occ?.email ?? ""]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      m.set(r.apartment_id, `${m.get(r.apartment_id) ?? ""} ${parts}`);
    }
    return m;
  }, [rooms, occupants]);

  // Client-side filter over the loaded apartments: label, floor, owner
  // (lessor) name, plus the room/tenant index above. Kept a simple substring
  // match — the list is small (a handful of units per property).
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return apartments;
    return apartments.filter((a) => {
      const owner = ownerNameFor(a) ?? "";
      return (
        a.label.toLowerCase().includes(q) ||
        (a.floor ?? "").toLowerCase().includes(q) ||
        owner.toLowerCase().includes(q) ||
        (roomTenantIndex.get(a.id) ?? "").includes(q)
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    apartments,
    search,
    lessorNameById,
    roomTenantIndex,
    property?.lessor_id,
  ]);

  // Next sort_order = max existing + 1 (keeps new apartments at the end).
  const nextSortOrder = useMemo(() => {
    const max = apartments.reduce((n, a) => Math.max(n, a.sort_order ?? 0), -1);
    return max + 1;
  }, [apartments]);

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {Array.from({ length: 2 }).map((_, i) => (
          <Skeleton key={i} className="h-52 w-full rounded-xl" />
        ))}
      </div>
    );
  }

  const editSheet = (
    <ApartmentEditSheet
      open={addOpen}
      onOpenChange={setAddOpen}
      propertyId={propertyId}
      nextSortOrder={nextSortOrder}
    />
  );

  if (apartments.length === 0) {
    return (
      <>
        <Card className="p-12 text-center border-dashed">
          <Building className="mx-auto h-8 w-8 text-muted-foreground/60 mb-3" />
          <p className="font-semibold">
            {t("admin.apartments.noApartmentsYet", "No apartments yet")}
          </p>
          <p className="text-sm text-muted-foreground mt-1">
            {t(
              "admin.apartments.noApartmentsYetDesc",
              "This property has no owner-owned apartments configured."
            )}
          </p>
          <Button size="sm" className="mt-4" onClick={() => setAddOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            {t("admin.apartments.addApartment", "Add apartment")}
          </Button>
        </Card>
        {editSheet}
      </>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          {search.trim()
            ? `${filtered.length} ${t("common.of", "of")} ${apartments.length} ${
                apartments.length === 1
                  ? t("admin.apartments.apartmentNoun", "apartment")
                  : t("admin.apartments.apartmentsNoun", "apartments")
              }`
            : `${apartments.length} ${
                apartments.length === 1
                  ? t("admin.apartments.apartmentNoun", "apartment")
                  : t("admin.apartments.apartmentsNoun", "apartments")
              } · ${t("admin.apartments.clickCardToOpenDetail", "click a card to open its detail.")}`}
        </p>
        <Button size="sm" variant="outline" onClick={() => setAddOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          {t("admin.apartments.addApartment", "Add apartment")}
        </Button>
      </div>
      {filtered.length === 0 ? (
        <Card className="p-10 text-center border-dashed">
          <Building className="mx-auto h-7 w-7 text-muted-foreground/60 mb-3" />
          <p className="font-medium">
            {t(
              "admin.apartments.noApartmentsMatch",
              "No apartments match “{{search}}”"
            ).replace("{{search}}", search)}
          </p>
          <p className="text-sm text-muted-foreground mt-1">
            {t(
              "admin.apartments.tryDifferentSearch",
              "Try a different apartment name, owner, room or tenant."
            )}
          </p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {filtered.map((apt) => (
            <ApartmentCard
              key={apt.id}
              apartment={apt}
              ownerName={ownerNameFor(apt)}
              currentMonthRevenue={currentMonthRevenueFor(apt)}
              missingRepresentor={missingRepIds.has(apt.id)}
            />
          ))}
        </div>
      )}
      {editSheet}
    </div>
  );
}
